import { useEffect, useRef, useState } from "react";
import { Bot, CalendarDays, Inbox, Users } from "lucide-react";
import { can, fmtDate, useLoad, useMe } from "./lib";

// Counts up to a number once, so a changed figure catches the eye. Shows the final value at once for reduced motion.
export function CountUp({ value, format = (n: number) => n.toLocaleString("en-US") }: { value: number; format?: (n: number) => string }) {
  const [shown, setShown] = useState(value);
  const from = useRef(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return setShown(value);
    const start = performance.now(), a = from.current, dur = 700;
    let raf = 0;
    const tick = (t: number) => {
      const k = Math.min(1, (t - start) / dur), e = 1 - Math.pow(1 - k, 3);
      setShown(Math.round(a + (value - a) * e));
      if (k < 1) raf = requestAnimationFrame(tick);
      else from.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{format(shown)}</>;
}

const daysUntil = (ymd: string) => Math.ceil((Date.parse(ymd + "T23:59:59-06:00") - Date.now()) / 86400000);

// The glanceable row at the top of Today: what needs you, the paying-users goal, what's coming up, how the agents ran.
export function TodayWidgets({ open, doneToday }: { open: any[] | null; doneToday: number }) {
  const { me } = useMe();
  const overview = useLoad<any>(me && can(me, "overview.company") ? "/api/overview" : null).data;
  const cal = useLoad<any>("/api/calendar").data;
  const agents = useLoad<any[]>(me && can(me, "users.manage") ? "/api/agents" : null).data;

  const urgent = (open ?? []).filter((d) => d.priority === "urgent" || d.priority === "high").length;
  const goal = overview?.goals?.[0];
  const metric = (k: string) => overview?.metrics?.find((m: any) => m.key === k);
  const total = goal?.current ?? null;
  const ios = metric("ios_paid_subscribers")?.value ?? null;
  const trials = metric("trials_active")?.value ?? null;
  const shownPaid = total ?? ios;
  const pct = goal && shownPaid != null ? Math.min(100, (shownPaid / goal.target) * 100) : 0;

  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago" }).format(new Date());
  const upcoming = (cal?.events ?? []).filter((e: any) => e.date >= today && e.status !== "done").sort((a: any, b: any) => (a.date + (a.time ?? "")).localeCompare(b.date + (b.time ?? ""))).slice(0, 3);
  const ran = (agents ?? []).filter((a) => a.runs?.[0]);
  const missed = (agents ?? []).filter((a) => a.missed).length;

  return (
    <div className="widgets">
      <a className="widget hero area-daily" href="#/" onClick={(e) => (e.preventDefault(), document.getElementById("decisions")?.scrollIntoView({ behavior: "smooth" }))}>
        <span className="w-label"><Inbox size={14} strokeWidth={2.2} aria-hidden />Needs you</span>
        <span className="w-big">{me ? <CountUp value={me.open_decisions} /> : "…"}</span>
        <span className="w-sub">{open ? (me && me.open_decisions ? `${open.length} to decide · ${me.open_decisions - open.length} tasks due · ${urgent} urgent` : `All clear · ${doneToday} decided today`) : "Loading"}</span>
      </a>

      {overview && (
        <a className="widget area-daily" href="#/overview" title={total == null ? "The combined count stays unknown until Google Play is connected." : undefined}>
          <span className="w-label"><Users size={14} strokeWidth={2.2} aria-hidden />Paying users</span>
          <div className="row" style={{ gap: 14, alignItems: "center" }}>
            <div className="ring" style={{ ["--p" as any]: pct }}><span>{shownPaid == null ? "?" : `${pct < 1 && pct > 0 ? "<1" : Math.round(pct)}%`}</span></div>
            <div className="stack" style={{ gap: 2 }}>
              <span className="w-big" style={{ fontSize: 28 }}>{shownPaid == null ? "Unknown" : <CountUp value={shownPaid} />}</span>
              <span className="w-sub">{total == null && ios != null ? "iOS only; Android unknown" : `of ${goal?.target?.toLocaleString("en-US") ?? "1,000"}`}</span>
            </div>
          </div>
          <span className="w-sub">{goal ? `${daysUntil(goal.due_on)} days to ${fmtDate(goal.due_on)}` : ""}{trials != null ? ` · ${trials} in trial` : ""}</span>
        </a>
      )}

      <a className="widget area-company" href="#/calendar">
        <span className="w-label"><CalendarDays size={14} strokeWidth={2.2} aria-hidden />Coming up</span>
        {upcoming.length ? (
          <div className="mini-list">
            {upcoming.map((e: any) => (
              <div key={e.id}><b>{e.date === today ? (e.time ?? "Today") : fmtDate(e.date)}</b><span title={e.title}>{e.title}</span></div>
            ))}
          </div>
        ) : (
          <span className="w-sub">{cal ? "Nothing dated in the portal this week." : "Loading"}</span>
        )}
      </a>

      {agents && (
        <a className="widget area-pr" href="#/agents">
          <span className="w-label"><Bot size={14} strokeWidth={2.2} aria-hidden />Agents</span>
          <span className="w-big" style={{ fontSize: 28 }}>{ran.length ? `${ran.length - missed} of ${agents.length}` : "Starting"}</span>
          <span className="w-sub">{ran.length ? (missed ? `${missed} missed a run · see receipts` : "ran on schedule · see receipts") : "Receipts start with tomorrow's runs."}</span>
        </a>
      )}
    </div>
  );
}
